import { supabase } from "@/integrations/supabase/client";
import { getSessionId } from "@/lib/session";
import { trackEvent } from "@/lib/audit";
import { generateVoucher, listInventory } from "@/lib/rewards/voucherEngine";
import { grantXpSecure } from "@/lib/antifraud/antifraud.functions";
import { getDeviceHash } from "@/lib/antifraud/deviceFingerprint";

export type LevelKey = "rookie" | "fan" | "analyst" | "pro" | "legend";

export interface LevelDef {
  key: LevelKey;
  label: string;
  min: number;
  emoji: string;
  color: string; // tailwind/oklch token
}

export const LEVELS: LevelDef[] = [
  { key: "rookie",  label: "Colaborador",       min: 0,    emoji: "🏅", color: "oklch(0.7 0.05 250)" },
  { key: "fan",     label: "Fan Activo",        min: 100,  emoji: "🏅", color: "oklch(0.75 0.15 200)" },
  { key: "analyst", label: "Fan Influyente",    min: 300,  emoji: "🏅", color: "oklch(0.78 0.18 145)" },
  { key: "pro",     label: "Líder de Afición",  min: 700,  emoji: "🏅", color: "oklch(0.78 0.2 50)" },
  { key: "legend",  label: "Embajador Mundial", min: 1500, emoji: "🏅", color: "oklch(0.85 0.18 85)" },
];

export const POINT_RULES: Record<string, number> = {
  prediction_vote: 10,
  chat_message: 5,
  goal_celebration: 15,
  sponsored_survey_submitted: 25,
  share_open: 8,
  onboarding_completed: 50,
  referral_signup: 100,
  app_open_daily: 20,
  match_pulse_answer: 5,
};

export function levelFor(points: number): { current: LevelDef; next: LevelDef | null; progress: number } {
  let current = LEVELS[0];
  for (const l of LEVELS) if (points >= l.min) current = l;
  const idx = LEVELS.indexOf(current);
  const next = idx < LEVELS.length - 1 ? LEVELS[idx + 1] : null;
  const progress = next
    ? Math.min(100, ((points - current.min) / (next.min - current.min)) * 100)
    : 100;
  return { current, next, progress };
}

export async function fetchPoints(): Promise<number> {
  const sid = getSessionId();
  const { data } = await supabase
    .from("loyalty_transactions")
    .select("amount")
    .eq("session_id", sid);
  return (data ?? []).reduce((s, r) => s + (r.amount as number), 0);
}

export async function awardPoints(reason: keyof typeof POINT_RULES | string, metadata: Record<string, unknown> = {}) {
  const sid = getSessionId();
  const amount = POINT_RULES[reason] ?? 0;
  if (!amount) return 0;
  // Daily uniqueness for app_open_daily
  if (reason === "app_open_daily") {
    const key = `ss2026_daily_${new Date().toISOString().slice(0, 10)}`;
    if (typeof window !== "undefined" && localStorage.getItem(key)) return 0;
    if (typeof window !== "undefined") localStorage.setItem(key, "1");
  }
  await supabase.from("loyalty_transactions").insert({
    session_id: sid,
    amount,
    reason,
    metadata: metadata as never,
  });
  // PR-34A — Server-authoritative XP grant. Server validates the reason
  // against an allow-list, enforces a per-reason cap, and rejects duplicates
  // via a unique idempotency_key. Failures here NEVER throw — the local
  // loyalty_transactions write above remains a visual ledger so existing UX
  // is unchanged when the user is anonymous or the network is offline.
  try {
    const { data: auth } = await supabase.auth.getUser();
    if (auth.user) {
      const day = new Date().toISOString().slice(0, 10);
      const idempotencyKey =
        reason === "app_open_daily"
          ? `xp:${auth.user.id}:app_open_daily:${day}`
          : `xp:${auth.user.id}:${reason}:${day}:${
              (metadata.idempotency_suffix as string | undefined) ??
              Math.random().toString(36).slice(2, 10)
            }`;
      const deviceHash = (await getDeviceHash()) ?? undefined;
      await grantXpSecure({
        data: { reason, amount, idempotencyKey, metadata: metadata as Record<string, unknown>, deviceHash },
      });
    }
  } catch {
    /* server-side XP grant is best-effort; client never blocks UX */
  }
  return amount;
}

export interface RewardCatalogItem {
  id: string;
  code: string;
  name: string;
  description: string | null;
  category: string;
  cost_points: number;
  sponsor: string | null;
  icon: string | null;
}

export async function fetchCatalog(): Promise<RewardCatalogItem[]> {
  const { data } = await supabase
    .from("rewards_catalog")
    .select("id,code,name,description,category,cost_points,sponsor,icon")
    .eq("is_active", true)
    .order("cost_points", { ascending: true });
  return (data as RewardCatalogItem[]) ?? [];
}

export async function redeemReward(item: RewardCatalogItem, currentPoints: number): Promise<{ ok: boolean; reason?: string }> {
  if (currentPoints < item.cost_points) return { ok: false, reason: "insufficient" };
  const sid = getSessionId();
  await supabase.from("reward_redemptions").insert({
    session_id: sid,
    reward_id: item.id,
    reward_code: item.code,
    cost_points: item.cost_points,
  });
  await supabase.from("loyalty_transactions").insert({
    session_id: sid,
    amount: -item.cost_points,
    reason: "reward_redeemed",
    metadata: { code: item.code, name: item.name } as never,
  });
  void trackEvent("reward_redeemed", { code: item.code, cost: item.cost_points });
  // Issue a real voucher backed by reward_inventory when an SKU match exists.
  // Falls back to a partner-less voucher so the user always gets a redeemable code.
  try {
    const inv = await listInventory();
    const match = inv.find((i) => i.sku === item.code) ?? null;
    await generateVoucher({
      inventoryId: match?.id ?? null,
      partnerId: match?.partner_id ?? null,
      rewardName: item.name,
      rewardCode: item.code,
      ttlDays: 30,
    });
  } catch (e) {
    console.warn("[gamification] voucher generation failed", e);
  }
  return { ok: true };
}